/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import useHandleFilterChange from '../hooks/useHandleFilterChange';
import useGetFiles from '../hooks/useGetFiles';
import useBreadCrumbs from '../hooks/useBreadCrumbs';
import useHandleSelectedRows from '../hooks/useHandleSelectedRows';

import constants, { defaultCaseFilter } from 'constants/index';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useNavbarContext } from 'context/NavbarContext';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { presignedURLGet } from '../services';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useDebounce } from 'hooks/useDebounce';
import { convertCaseToFilterFormat } from 'utils/utils';
import { totalCountEvent } from 'services/generalServices';
import { listGlobalTags } from 'services/Settings';
import useGetTags from '../hooks/useGetTags';
import { courtDateEvent, summarizeDocument } from 'services/fileManager/fileManagerServices';

const FileMangerContext = createContext({});

export const useFileManagerContext = () => {
  const context = useContext(FileMangerContext);

  if (context === undefined) {
    throw new Error('useTestContext must be used within TestProvider');
  }

  return context;
};

export const FileManagerContextProvider = (props) => {
  const {
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isClientFileManager,
    isFirmDocuments,
    caseDetails,
    isClosed,
    onMountUnMount,
    onOperationBreak,
  } = props;

  const userContext = useContext(UserDetailsContext);
  const { readClientNotifications, notificationCounts } = useNavbarContext();
  const location = useLocation();
  const { addToast } = useToast();

  const getDefaultFilter = () => {
    // Check URL search parameters for shared_folder flag
    const urlParams = new URLSearchParams(location.search);
    const sharedFolderFromUrl = urlParams.get('shared_folder') === 'true';

    // For case files, client files, and lead files, use old simple filter structure
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      let filter = {
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: location?.state?.breadCrumb ? location?.state?.breadCrumb?.value : '',
        fileStatus: '',
      };

      // Add shared_folder flag if set in URL or for client users
      if (sharedFolderFromUrl || (userContext?.access_level === 'client' && location?.state?.isFromClientDashboard)) {
        filter.shared_folder = true;
      }

      return filter;
    }

    // For global file manager and firm documents, use new filter structure
    let filter = {
      ...defaultCaseFilter,
      case: convertCaseToFilterFormat(userContext?.clientSelectedCase) || '',
      selectedFolder: location?.state?.breadCrumb ? location?.state?.breadCrumb?.value : '',
    };

    // If this is the Firm Documents page, set default filter to "Client Uploads"
    // if (isFirmDocuments) {
    //   filter.uploadedBy = 'client';
    //   filter.is_client_uploaded = true;
    // }

    let file = location?.state?.file;
    if (location?.state?.isFromClientDashboard && file) {
      const caseVal = { label: file?.case_name, id: file?.case_id };
      filter.case = caseVal;
    }

    // Add shared_folder flag if set in URL or for client users from dashboard
    if (sharedFolderFromUrl || (userContext?.access_level === 'client' && location?.state?.isFromClientDashboard)) {
      filter.shared_folder = true;
    }
    // If coming from dashboard signatures, set filter to show signed documents
    if (location?.state?.isFromDashboardSignatures) {
      filter.esignature_status = 'SIGNED';
    }

    return filter;
  };
  // hooks
  const { fileManagerFilters, onFilterChange, onTagChange, setFileManagerFilters } = useHandleFilterChange(getDefaultFilter);
  const { files, getFiles, pageLimit, setPageLimit, paginationDirection, onNextPageClick, onPrevPageClick, nextToken, transitionToken } =
    useGetFiles(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext);
  const { getFileTags, fullTags, isLoading: isTagsLoading } = useGetTags();
  const websocketContext = useContext(WebSocketConnectionContext);

  const { breadCrumbs, setBreadCrumbs, handleBreadCrumbs, filterBreadCrumbs } = useBreadCrumbs();
  const { selectedRows, selectAll, handleSingleCheckboxClick, handleSelectAllCheckboxClick, setSelectedRows } = useHandleSelectedRows();

  //states
  const [currentView, setCurrentView] = useState('list');
  const [overrideFiles, setOverrideFiles] = useState(null);
  const [isContentSearchActive, setIsContentSearchActive] = useState(false);

  const resetContentSearch = () => {
    setOverrideFiles(null);
    setIsContentSearchActive(false);
  };
  const [selectedItem, setSelectedItem] = useState();
  const [fileName, setFileName] = useState('');
  const [deletePermission, setDeletePermission] = useState(true);
  const [versionFileUpload, setVersionFileUpload] = useState(false);
  const [previewList, setPreviewList] = useState([]);
  const [selectedPreviewItem, setSelectedPreviewItem] = useState();
  const [query, setQuery] = useState('');
  const [valueSelected, setValueSelected] = useState(false);

  // modal states
  const [showEsignStatusModal, setEsignStatusModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [tagModalType, setTagModalType] = useState('');
  const [showShareFileModal, setShowShareFileModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [moveItemModal, setMoveItemModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [isCopy, setIsCopy] = useState(false);

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showUploadedVersionsModal, setShowUploadedVersionsModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showCreateDocumentModal, setShowCreateDocumentModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [globalTags, setGlobalTags] = useState(null);
  const [showSummarizeModal, setShowSummarizeModal] = useState(false);
  const [showConfigureSummaryModal, setShowConfigureSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryVersions, setSummaryVersions] = useState({});
  const [summaryGenerationStatus, setSummaryGenerationStatus] = useState({});
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [courtDateMsg, setCourtDateMsg] = useState(null);

  const [showSendFaxModal, setShowSendFaxModal] = useState(false);
  const [isFirmDocumentsUpload, setIsFirmDocumentsUpload] = useState(false);

  const filterDebounced = useDebounce(query, 400);

  // Check for dashboard signatures navigation and update filters accordingly
  useEffect(() => {
    if (location?.state?.isFromDashboardSignatures) {
      setFileManagerFilters((prevFilters) => ({
        ...prevFilters,
        esignature_status: 'SIGNED',
      }));
    }
  }, [location?.state?.isFromDashboardSignatures, setFileManagerFilters]);

  //For global search redirection and file preview
  useEffect(() => {
    // Check for file_path or object_key (fallback for older data structures)
    const filePath = location?.state?.file?.file_path || location?.state?.file?.object_key;
    if (filePath) {
      let file = location?.state?.file;
      // Ensure file_path is set (in case object_key was used)
      if (!file.file_path && file.object_key) {
        file.file_path = file.object_key;
      }
      // Ensure object_id is set for consistency
      if (!file.object_id && (file.file_id || file.document_id)) {
        file.object_id = file.file_id || file.document_id;
      }
      // need to make additional changes in report generator page to get file url
      presignedURLGet(filePath, file?.version_id, false, true)
        .then((res) => {
          file.url = res.data?.file_url;
          setShowPreview(true);
          setSelectedItem(file);
          setSelectedPreviewItem(file);
          setPreviewList([file]);
        })
        .catch((err) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File Not Found');
        });
    }
    if (location?.state?.breadCrumb) {
      const folderId = location?.state?.breadCrumb?.value;
      setBreadCrumbs([{ label: location?.state?.breadCrumb?.label, value: folderId }]);
      // Update selectedFolder filter to ensure API is called with the correct folder_id
      if (folderId) {
        setFileManagerFilters((prevFilters) => ({
          ...prevFilters,
          selectedFolder: folderId,
          folder_id: folderId,
        }));
      }
    }
  }, [location?.state]);

  // Track previous case ID from pathname to detect case changes
  const prevCaseIdRef = useRef(null);
  // Track previous res_type_id to detect folder changes
  const prevResTypeIdRef = useRef(null);

  // Reset breadcrumbs and selectedFolder filter when case changes (detected by pathname change)
  useEffect(() => {
    if (isCaseFileManager || isLeadFileManager) {
      // Extract case ID from pathname (e.g., /cases/{caseId}/files or /leads/{leadId}/files)
      const pathParts = location?.pathname?.split('/');
      const caseTypeIndex = pathParts.findIndex((part) => part === 'cases' || part === 'leads');
      const pathnameCaseId = caseTypeIndex !== -1 && pathParts[caseTypeIndex + 1] ? pathParts[caseTypeIndex + 1] : null;

      // Reset breadcrumbs and selectedFolder when:
      // 1. We have a case ID in the pathname
      // 2. The pathname case ID is different from the previous one (case changed)
      // 3. We're not coming from a search with breadcrumb state (to preserve search navigation breadcrumbs)
      if (pathnameCaseId && pathnameCaseId !== prevCaseIdRef.current && !location?.state?.breadCrumb) {
        setBreadCrumbs([]);
        // Also reset selectedFolder filter to ensure files load correctly when clicking home
        setFileManagerFilters((prevFilters) => ({
          ...prevFilters,
          selectedFolder: '',
          folder_id: '',
        }));
        prevCaseIdRef.current = pathnameCaseId;
      } else if (pathnameCaseId && !prevCaseIdRef.current) {
        // Initialize the ref on first load
        prevCaseIdRef.current = pathnameCaseId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, isCaseFileManager, isLeadFileManager]);

  // Reset breadcrumbs when res_type_id changes (different folder from search)
  useEffect(() => {
    const currentResTypeId = location?.state?.res_type_id;
    if (currentResTypeId && currentResTypeId !== prevResTypeIdRef.current) {
      setBreadCrumbs([]);
      // Reset selectedFolder filter to ensure files load correctly
      // This will trigger the API call via the useEffect that watches fileManagerFilters
      setFileManagerFilters((prevFilters) => ({
        ...prevFilters,
        selectedFolder: '',
        folder_id: '',
      }));
      prevResTypeIdRef.current = currentResTypeId;
    } else if (currentResTypeId && !prevResTypeIdRef.current) {
      // Initialize the ref on first load
      prevResTypeIdRef.current = currentResTypeId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.res_type_id, setFileManagerFilters]);

  useEffect(() => {
    if (!location?.state?.file) {
      const viewFileData = localStorage.getItem('viewFile');
      if (viewFileData) {
        try {
          const file = JSON.parse(viewFileData);
          const actualFilePath = file?.file_path || file?.object_key;

          if (actualFilePath) {
            if (!file.file_path && file.object_key) {
              file.file_path = file.object_key;
            }
            if (!file.object_id && (file.file_id || file.document_id)) {
              file.object_id = file.file_id || file.document_id;
            }

            localStorage.removeItem('viewFile');

            presignedURLGet(actualFilePath, file?.version_id, false, true)
              .then((res) => {
                console.log('res inside presigned url from localStorage', res);
                file.url = res.data?.file_url;
                setShowPreview(true);
                setSelectedItem(file);
                setSelectedPreviewItem(file);
                setPreviewList([file]);
              })
              .catch((err) => {
                addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'File Not Found');
              });
          }
        } catch (e) {
          console.error('Error parsing viewFile from localStorage:', e);
          localStorage.removeItem('viewFile');
        }
      }
    }
  }, []);

  useEffect(() => {
    if (userContext.permissions) {
      if (userContext.getPermission('cases') !== constants.permissions.WRITE && props.casePage) {
        setDeletePermission(false);
      } else if (userContext.getFirmPermission(constants.permissions.DELETE_ITEMS) === false) {
        setDeletePermission(false);
      } else {
        setDeletePermission(true);
      }
    }
  }, [userContext.permissions]);

  //Client portal notification mark as complete
  useEffect(() => {
    if (userContext?.userDetails?.contact_id && isClientFileManager && notificationCounts) {
      Boolean(notificationCounts?.file_count) && readClientNotifications('file', userContext.userDetails.contact_id);
    }
  }, [userContext?.userDetails, notificationCounts, isClientFileManager]);

  useEffect(() => {
    handleGetFiles(pageLimit);
  }, [fileManagerFilters, isClientFileManager, filterDebounced, caseDetails?.case_id, location?.pathname, location?.state?.res_type_id]);

  useEffect(() => {
    getFileTags(caseDetails?.case_id || '');
  }, [caseDetails?.case_id]);

  useEffect(() => {
    const wsData = websocketContext?.aiFileSummary;
    if (!wsData) return;

    const currentSummary = wsData?.body?.summary;
    if (currentSummary?.file_id && currentSummary?.isError === false) {
      const fileId = currentSummary.file_id;

      let normalizedStatus = currentSummary?.summary_generation_status;
      if (currentSummary?.summary_generation_status && typeof currentSummary?.summary_generation_status === 'string') {
        const statusFromWS = currentSummary.summary_generation_status.toLowerCase();
        normalizedStatus = statusFromWS;
        if (statusFromWS === 'in progress' || statusFromWS === 'in_progress') {
          normalizedStatus = 'in_progress';
        }
      }

      setSummaryGenerationStatus((prev) => {
        const updated = {
          ...prev,
          [fileId]: {
            summary_generation_status: normalizedStatus,
            file_id: fileId,
            version_id: currentSummary.version_id,
            file_path: currentSummary.file_path,
            s3_path: currentSummary.s3_path,
            error_status: normalizedStatus === 'failed' || currentSummary.isError === true,
            ...currentSummary,
          },
        };
        return updated;
      });
      return;
    }

    const summaryVersions = wsData?.body?.summary_versions;
    if (summaryVersions && Array.isArray(summaryVersions) && summaryVersions?.length > 0) {
      const latestSummary = summaryVersions[summaryVersions?.length - 1];
      const filePath = latestSummary?.parameters?.file_path;
      const summaryVersionId = latestSummary?.version_id;
      const fileId = latestSummary?.parameters?.file_id;

      if (!filePath && !summaryVersionId && !fileId) {
        return;
      }

      const isError = latestSummary.error_status === true;
      const hasS3Path = latestSummary.s3_path && latestSummary.s3_path.length > 0;
      const summaryStatus = isError ? 'failed' : hasS3Path ? 'success' : 'in_progress';

      if (fileId) {
        setSummaryGenerationStatus((prev) => {
          const updated = {
            ...prev,
            [fileId]: {
              summary_generation_status: summaryStatus,
              summary_status: summaryStatus === 'success' ? 'ready' : summaryStatus === 'in_progress' ? 'processing' : 'failed',
              s3_path: latestSummary?.s3_path,
              error_status: isError,
              summary_versions: summaryVersions,
              file_id: fileId,
              version_id: summaryVersionId,
              file_path: filePath,
              ...latestSummary,
            },
          };
          return updated;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocketContext?.aiFileSummary]);

  const getGlobalTags = async () => {
    totalCountEvent('global_tag').then((response) => {
      listGlobalTags(response.data)
        .then((response) => {
          let tagsArray = [];
          response?.data?.global_tag.forEach((item) => {
            tagsArray.push({ name: item.tag, value: item.tag });
          });
          setGlobalTags(tagsArray);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  };

  const handleGetFiles = async (pageLimit, folder_id) => {
    let case_id;
    let shouldIncludeCaseId = true;

    // Handle case_id logic - check isFirmDocuments first
    if (isFirmDocuments) {
      // Firm Documents page - ALWAYS include case_id as undefined regardless of any other filters
      case_id = undefined;
      shouldIncludeCaseId = true;
    } else if (isGlobalFileManager) {
      if (fileManagerFilters?.uploadedBy === 'all' || !fileManagerFilters?.uploadedBy) {
        // All Files - do NOT include case_id at all
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else if (fileManagerFilters?.is_client_uploaded === true) {
        // Client Uploads - do NOT include case_id
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else if (fileManagerFilters?.isFirmUploads) {
        // Firm Uploads - include case_id as undefined
        case_id = undefined;
        shouldIncludeCaseId = true;
      } else if (fileManagerFilters?.forceCaseUndefined) {
        // Legacy firm uploads logic - do NOT include case_id
        case_id = undefined;
        shouldIncludeCaseId = false;
      } else {
        case_id = fileManagerFilters?.case?.id;
        shouldIncludeCaseId = true;
      }
    } else if (isClientFileManager) {
      if (userContext?.clientCaseList?.length === 1) {
        case_id = userContext?.clientCaseList[0]?.case_id;
      } else {
        // For client file manager, use the case from filters or fallback to clientSelectedCase
        case_id = fileManagerFilters?.case?.id || userContext?.clientSelectedCase?.case_id;
      }
      shouldIncludeCaseId = true;
    } else {
      case_id = caseDetails?.case_id;
      shouldIncludeCaseId = true;
    }

    let obj = {
      selectedFolder: folder_id || fileManagerFilters.selectedFolder,
      selectedType: fileManagerFilters.fileType,
      tag: fileManagerFilters.tag,
      query: query,
      pagination_direction: paginationDirection,
      is_read: fileManagerFilters?.fileStatus,
    };

    // Include shared_folder flag if it exists in filters
    if (fileManagerFilters?.shared_folder) {
      obj.shared_folder = fileManagerFilters.shared_folder;
    }

    // Include shared_contact_list only if it exists and has values
    if (
      fileManagerFilters?.shared_contact_list &&
      Array.isArray(fileManagerFilters.shared_contact_list) &&
      fileManagerFilters.shared_contact_list.length > 0
    ) {
      obj.shared_contact_list = fileManagerFilters.shared_contact_list;
    }

    // Include folder_shared only if it's explicitly true
    if (fileManagerFilters?.folder_shared === true) {
      obj.folder_shared = fileManagerFilters.folder_shared;
    }

    // Only include case_id if it should be included
    // console.log("shouldIncludeCaseId", shouldIncludeCaseId, case_id);
    if (shouldIncludeCaseId && case_id) {
      obj.case_id = case_id;
    }

    // For case files, client files, and lead files, use old field names
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      obj.is_read = fileManagerFilters?.fileStatus;
    } else {
      // For global file manager and firm documents, use new field names
      obj.is_read = fileManagerFilters?.is_read;
      obj.start_date = fileManagerFilters?.start_date;
      obj.end_date = fileManagerFilters?.end_date;

      // Only send is_client_uploaded if it's actually true (for client uploads)
      if (fileManagerFilters?.is_client_uploaded === true) {
        obj.is_client_uploaded = fileManagerFilters?.is_client_uploaded;
      }

      obj.esignature_status = fileManagerFilters?.esignature_status;
    }
    getFiles(obj, currentView, pageLimit);
  };

  useEffect(() => {
    if (files && Array.isArray(files) && files?.length > 0) {
      setSummaryGenerationStatus((prev) => {
        const updated = { ...prev };
        let hasUpdates = false;

        files?.forEach((file) => {
          const fileId = file?.file_id;
          if (!fileId) return;

          if (file?.summary?.summary_generation_status) {
            let normalizedStatus = file.summary.summary_generation_status;
            if (typeof file.summary.summary_generation_status === 'string') {
              const apiStatus = file.summary.summary_generation_status.toLowerCase();
              normalizedStatus = apiStatus;
              if (apiStatus === 'in progress' || apiStatus === 'in_progress') {
                normalizedStatus = 'in_progress';
              }
            }

            const existingStatus = updated[fileId];
            const apiStatusObj = {
              summary_generation_status: normalizedStatus,
              file_id: fileId,
              version_id: file?.version_id,
              file_path: file?.file_path,
              s3_path: file?.summary?.s3_path,
              error_status: normalizedStatus === 'failed' || file?.summary?.isError === true,
              ...file?.summary,
            };

            if (
              !existingStatus ||
              existingStatus.summary_generation_status !== normalizedStatus ||
              existingStatus.s3_path !== apiStatusObj.s3_path
            ) {
              updated[fileId] = {
                ...existingStatus,
                ...apiStatusObj,
              };
              hasUpdates = true;
            }
          }
        });

        return hasUpdates ? updated : prev;
      });
    }
  }, [files]);

  function onResetFilter() {
    if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
      // Case files, client files, and lead files - use old simple filter structure
      setFileManagerFilters((preVal) => ({
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: '',
        fileStatus: '',
        // Preserve shared_folder flag for client users
        ...(userContext?.access_level === 'client' && { shared_folder: true }),
      }));
    } else {
      // Global file manager and firm documents - use new filter structure
      setFileManagerFilters((preVal) => ({
        fileType: '',
        tag: '',
        case: isClientFileManager ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '',
        selectedFolder: '',
        is_read: '',
        start_date: '',
        end_date: '',
        uploadedBy: isFirmDocuments ? 'client' : '', // Keep default for firm documents
        is_client_uploaded: isFirmDocuments ? false : '', // Keep default for firm documents
        esignature_status: '',
        forceCaseUndefined: false,
        isFirmUploads: false,
        // Preserve shared_folder flag for client users
        ...(userContext?.access_level === 'client' && { shared_folder: true }),
      }));
    }
    setQuery('');
    setBreadCrumbs([]);
  }

  const handleSummarizeFile = (rowData, data) => {
    setSelectedItem(rowData);
    if (Object.keys(data)?.length > 0) {
      const statusKey = rowData.file_id;

      summarizeDocument(data)
        .then((response) => {
          const res = response?.data;
          if (statusKey && res === 'Summary is being generated') {
            setSummaryGenerationStatus((prev) => ({
              ...prev,
              [statusKey]: {
                summary_generation_status: 'in_progress',
                summary_status: 'processing',
                file_id: rowData.file_id,
                version_id: rowData.version_id,
                file_path: rowData.file_path,
              },
            }));
          }
        })
        .catch((error) => {
          console.log(error);
          if (statusKey) {
            setSummaryGenerationStatus((prev) => ({
              ...prev,
              [statusKey]: {
                ...prev[statusKey],
                summary_generation_status: 'failed',
                summary_status: 'failed',
                error_status: true,
              },
            }));
          }
        });
    }
  };

  const handleGenerateConfigureSummary = (config) => {
    const { format, length, rowData } = config;
    setShowConfigureSummaryModal(false);
    const data = {
      file_path: rowData?.file_path,
      file_name: rowData?.file_name,
      file_id: rowData?.file_id,
      version_id: rowData?.version_id,
      sk: rowData?.sk,
      regenerate: 1,
      summary_option: format,
      summary_percentage: length,
      timezone: userContext?.userDetails?.timezone,
    };
    handleSummarizeFile(rowData, data);
  };

  const handleShowSummaryModal = (rowData) => {
    setSelectedItem(rowData);

    let summaryVersionsArray = rowData?.summary_versions;

    const wsData = websocketContext?.aiFileSummary;
    let wsSummaryVersions = null;
    let wsCurrentSummary = null;

    if (wsData && rowData?.file_id) {
      const wsFileId = wsData?.body?.summary?.file_id;
      const wsFilePath = wsData?.body?.summary_versions?.[wsData?.body?.summary_versions.length - 1]?.parameters?.file_path;

      if (wsFileId === rowData.file_id || wsFilePath === rowData.file_path) {
        wsSummaryVersions = wsData.body?.summary_versions;
        wsCurrentSummary = wsSummaryVersions[wsSummaryVersions.length - 1];
      }
    }

    const summaryExistsInArray = (summary, array) => {
      if (!summary || !array) return false;
      return array.some((existing) => {
        const summaryVersionId = summary?.version_id;
        const existingVersionId = existing?.version_id;
        if (summaryVersionId && existingVersionId && summaryVersionId === existingVersionId) {
          return true;
        }
        const summaryS3Path = summary?.s3_path;
        const existingS3Path = existing?.s3_path;
        if (summaryS3Path && existingS3Path && summaryS3Path === existingS3Path) {
          return true;
        }
        return false;
      });
    };

    if (wsCurrentSummary && !summaryExistsInArray(wsCurrentSummary, summaryVersionsArray || [])) {
      if (wsSummaryVersions && Array.isArray(wsSummaryVersions) && wsSummaryVersions?.length > 0) {
        summaryVersionsArray = wsSummaryVersions;
      } else if (summaryVersionsArray && Array.isArray(summaryVersionsArray) && summaryVersionsArray?.length > 0) {
        summaryVersionsArray = [...summaryVersionsArray, wsCurrentSummary];
      } else {
        summaryVersionsArray = [wsCurrentSummary];
      }
    } else if (wsSummaryVersions && Array.isArray(wsSummaryVersions) && wsSummaryVersions.length > 0) {
      if (!summaryVersionsArray || !Array.isArray(summaryVersionsArray) || summaryVersionsArray.length === 0) {
        summaryVersionsArray = wsSummaryVersions;
      }
    }

    if (summaryVersionsArray && Array.isArray(summaryVersionsArray) && summaryVersionsArray.length > 0) {
      const currentSummary = summaryVersionsArray[summaryVersionsArray.length - 1];

      if (currentSummary?.s3_path && currentSummary.error_status === false) {
        const allSuccessfulVersions = summaryVersionsArray?.filter((summary) => summary.s3_path && summary.error_status === false);

        if (allSuccessfulVersions?.length > 0) {
          const previousVersions = allSuccessfulVersions?.slice(0, -1); // All except the last one

          setSummaryData(currentSummary);
          setSummaryVersions(previousVersions);
          setShowSummarizeModal(true);
          return;
        }
      }
    }
  };

  const handleOpenConfigureSummaryModal = (rowData) => {
    setSelectedItem(rowData);
    setShowConfigureSummaryModal(true);
  };

  const handleShowCreateEvent = (rowData, data) => {
    setShowCreateEventModal(true);
    setSelectedItem(rowData);
    if (Object.keys(data)?.length > 0) {
      courtDateEvent(data)
        .then((response) => {
          const res = response?.data;
          setCourtDateMsg(res);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  function hideCreateEventModal() {
    setShowCreateEventModal(false);
    setCourtDateMsg();
  }

  const modals = {
    showPreview,
    setShowPreview,
    showRenameModal,
    setShowRenameModal,
    moveItemModal,
    setMoveItemModal,
    showDeleteModal,
    showBulkDeleteModal,
    showBulkMoveModal,
    setShowDeleteModal,
    setShowBulkDeleteModal,
    setShowBulkMoveModal,
    showShareFileModal,
    setShowShareFileModal,
    tagModalType,
    setTagModalType,
    showAddTag,
    setShowAddTag,
    showFileUploadModal,
    setShowFileUploadModal,
    showUploadedVersionsModal,
    setShowUploadedVersionsModal,
    showCreateFolderModal,
    setShowCreateFolderModal,
    showCreateDocumentModal,
    setShowCreateDocumentModal,
    showSignatureModal,
    setShowSignatureModal,
    isCopy,
    setIsCopy,
    setEsignStatusModal,
    showEsignStatusModal,
    showSummarizeModal,
    setShowSummarizeModal,
    showConfigureSummaryModal,
    setShowConfigureSummaryModal,
    showCreateEventModal,
    setShowCreateEventModal,

    showSendFaxModal,
    setShowSendFaxModal,
    isFirmDocumentsUpload,
    setIsFirmDocumentsUpload,
  };

  // When content search is activated, override visible files list
  const visibleFiles = overrideFiles ?? files;

  const contextPayload = useMemo(
    () => ({
      // props
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      // filter
      fileManagerFilters,
      onFilterChange,
      onResetFilter,
      // view
      currentView,
      setCurrentView,
      // files
      files: visibleFiles,
      getFiles,
      handleGetFiles,
      pageLimit,
      setPageLimit,
      // selected rows
      selectedRows,
      selectAll,
      handleSingleCheckboxClick,
      handleSelectAllCheckboxClick,
      //breadcrumbs
      breadCrumbs,
      setBreadCrumbs,
      handleBreadCrumbs,
      filterBreadCrumbs,
      // selected item
      selectedItem,
      setSelectedItem,
      // filename
      fileName,
      setFileName,
      // case details
      caseDetails,
      isClosed,
      // delete permissions
      deletePermission,
      modals,
      versionFileUpload,
      setVersionFileUpload,
      previewList,
      setPreviewList,
      selectedPreviewItem,
      setSelectedPreviewItem,
      onNextPageClick,
      onPrevPageClick,
      nextToken,
      transitionToken,
      paginationDirection,
      setSelectedRows,
      onTagChange,
      fullTags,
      query,
      setQuery,
      onMountUnMount,
      onOperationBreak,
      setFileManagerFilters,
      globalTags,
      getGlobalTags,
      getFileTags,
      handleSummarizeFile,
      handleGenerateConfigureSummary,
      handleShowSummaryModal,
      handleOpenConfigureSummaryModal,
      summaryData,
      setSummaryData,
      summaryVersions,
      summaryGenerationStatus,
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,
      isTagsLoading,
      // content search controls
      isContentSearchActive,
      setIsContentSearchActive,
      setOverrideFiles,
      resetContentSearch,
    }),
    [
      fileManagerFilters,
      setFileManagerFilters,
      files,
      overrideFiles,
      visibleFiles,
      pageLimit,
      currentView,
      selectedRows,
      selectAll,
      breadCrumbs,
      showPreview,
      showShareFileModal,
      selectedItem,
      fileName,
      showAddTag,
      caseDetails,
      tagModalType,
      isClosed,
      deletePermission,
      showRenameModal,
      moveItemModal,
      showDeleteModal,
      showBulkDeleteModal,
      showBulkMoveModal,
      showFileUploadModal,
      showUploadedVersionsModal,
      versionFileUpload,
      previewList,
      selectedPreviewItem,
      showCreateFolderModal,
      showCreateDocumentModal,
      nextToken,
      transitionToken,
      paginationDirection,
      showSignatureModal,
      setSelectedRows,
      fullTags,
      query,
      onMountUnMount,
      onOperationBreak,
      valueSelected,
      setValueSelected,
      globalTags,
      getFileTags,
      showSummarizeModal,
      showConfigureSummaryModal,
      handleSummarizeFile,
      handleGenerateConfigureSummary,
      handleShowSummaryModal,
      handleOpenConfigureSummaryModal,
      summaryData,
      setSummaryData,
      summaryVersions,
      summaryGenerationStatus,
      handleShowCreateEvent,
      hideCreateEventModal,
      courtDateMsg,
      isTagsLoading,
      isFirmDocumentsUpload,
      isContentSearchActive,
      resetContentSearch,
    ]
  );

  return <FileMangerContext.Provider value={contextPayload} {...props} />;
};
